// gateDraftStore — keeps in-flight Keep/Edit/Reject decisions for each
// gate so that navigating away (timeline rail, focus change) and coming
// back doesn't wipe what the architect already entered.
//
// Each gate component used to hold its decisions in local useState seeded
// from the backend gate payload. When the focus changed the component
// unmounted and the local edits were lost. This store hoists that array
// out of the component, keyed by `${projectId}:${runId}:${gateNumber}`,
// and persists to localStorage so even a reload keeps the draft.
//
// Re-emit clears the draft for that gate so the freshly regenerated
// server payload reseeds the editor.
//
// ----- Stale-draft hygiene (bug fix) ---------------------------------- //
// localStorage drafts can outlive the project that produced them. Two
// real-world scenarios that surfaced this leak:
//   1. The user deletes a project on disk, then later creates a new
//      project whose generated id collides with the deleted one (the
//      id is `proj_<YYYY_MM_DD>_<slug>`, so re-creating the same name
//      on the same day collides). The old project's drafts are still
//      sitting in localStorage and the new project's gate components
//      pick them up, surfacing pre-selected answers and "8 decisions"
//      badges that the user never authored.
//   2. The architect makes manual edits at G1, then re-emits the gate
//      after a charter rewrite that changes the deferred-unknown set.
//      The decision IDs in the draft no longer match the IDs the
//      server emitted, but the old answers still flicker into view
//      until the next manual interaction.
//
// We solve both by *reconciling* the draft against the freshly fetched
// server gate before treating it as authoritative — see
// :func:`reconcileDraftWithGate` below. Drafts whose decision-id set is
// not a subset of the server's are treated as stale and discarded.
// ---------------------------------------------------------------------- //

import { create } from 'zustand'
import { persist, type StateStorage } from 'zustand/middleware'
import type { Gate, GateDecisionRecord } from '@/types'

export type GateDraftKey = string

export function gateDraftKey(
  projectId: string,
  runId: number,
  gateNumber: number,
): GateDraftKey {
  return `${projectId}:${runId}:${gateNumber}`
}

interface GateDraftStore {
  drafts: Record<GateDraftKey, GateDecisionRecord[]>
  setDraft: (key: GateDraftKey, decisions: GateDecisionRecord[]) => void
  clearDraft: (key: GateDraftKey) => void
  /**
   * Drop every draft whose key starts with ``${projectId}:`` — used by
   * the project-delete path so a freshly recreated project with the
   * same id doesn't inherit the deleted project's pre-selected
   * answers.
   */
  clearDraftsForProject: (projectId: string) => void
}

// ---------------------------------------------------------------------- //
// QuotaExceededError mitigation (May 2026 bug fix)
//
// Before this fix the persisted slice stored every field of every
// GateDecisionRecord, including the full ``payload`` server panel data
// (~1-2 KB per finding × 265 G4 findings = ~500 KB per project per
// gate). Across multiple projects and multiple gates this easily
// exceeded the browser's 5-10 MB localStorage quota, and the next
// ``setItem`` call threw ``QuotaExceededError`` from
// ``ContentAwareG4Gate.tsx`` mid-render. Two complementary fixes:
//
//   1. **Persist a slim shape only.** ``payload`` and ``question``
//      are server-derived and re-fetched on every gate load via
//      ``reconcileDraftWithGate``. We strip both before persist via
//      ``partialize`` so the on-disk row carries only the
//      architect's mutated fields. In-memory state still has the
//      full shape — consumers don't notice.
//
//   2. **Evict-and-retry on quota error.** Even with stripping, an
//      architect who's been working through many projects can
//      eventually hit the quota. ``quotaSafeStorage`` wraps the
//      default ``localStorage`` so a ``setItem`` failure evicts the
//      stored draft blob entirely (a single-row reset is preferable
//      to losing the entire session). The cleared blob means the
//      next page load reseeds from the server — the architect loses
//      the *draft* but not any *submitted* gate.
// ---------------------------------------------------------------------- //

/** Fields the persisted slice keeps per decision. ``payload`` and
 *  ``question`` are deliberately omitted — they come from the server
 *  on the next gate fetch via ``reconcileDraftWithGate``. */
type PersistedDecision = Pick<
  GateDecisionRecord,
  'id' | 'answer' | 'status' | 'skip_reason' | 'rejection_reason'
>

function slimDecision(d: GateDecisionRecord): PersistedDecision {
  return {
    id: d.id,
    answer: d.answer ?? null,
    status: d.status,
    skip_reason: d.skip_reason ?? null,
    rejection_reason: d.rejection_reason ?? null,
  }
}

/** localStorage wrapper that recovers from ``QuotaExceededError``.
 *  Strategy: on a quota error during ``setItem``, drop the key
 *  entirely and retry once. If still failing, log + swallow so the
 *  app doesn't crash. The architect's in-memory state survives;
 *  only the persisted blob is reset. */
const quotaSafeStorage: StateStorage = {
  getItem: (name) => {
    try {
      return localStorage.getItem(name)
    } catch {
      return null
    }
  },
  setItem: (name, value) => {
    try {
      localStorage.setItem(name, value)
    } catch (err) {
      // Identify quota errors across browsers — name/code vary.
      const isQuota =
        err instanceof DOMException &&
        (err.name === 'QuotaExceededError' ||
          err.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
          err.code === 22 ||
          err.code === 1014)
      if (!isQuota) {
        // Some non-quota IO failure — swallow but log.
        console.warn('gateDraftStore: localStorage.setItem failed', err)
        return
      }
      // Recovery — wipe the key and retry once. If retry still
      // fails (unlikely with the slim shape) swallow so the app
      // doesn't blow up mid-render.
      try {
        localStorage.removeItem(name)
        localStorage.setItem(name, value)
        console.warn(
          'gateDraftStore: QuotaExceededError — purged the stored ' +
            'draft blob and retried successfully',
        )
      } catch (retryErr) {
        console.warn(
          'gateDraftStore: QuotaExceededError persists after purge ' +
            '— giving up on persisting drafts this session',
          retryErr,
        )
      }
    }
  },
  removeItem: (name) => {
    try {
      localStorage.removeItem(name)
    } catch {
      // ignore
    }
  },
}

export const useGateDraftStore = create<GateDraftStore>()(
  persist(
    (set) => ({
      drafts: {},
      setDraft: (key, decisions) =>
        set((s) => ({ drafts: { ...s.drafts, [key]: decisions } })),
      clearDraft: (key) =>
        set((s) => {
          if (!(key in s.drafts)) return s
          const { [key]: _drop, ...rest } = s.drafts
          return { drafts: rest }
        }),
      clearDraftsForProject: (projectId) =>
        set((s) => {
          const prefix = `${projectId}:`
          const next: Record<GateDraftKey, GateDecisionRecord[]> = {}
          let removed = 0
          for (const [k, v] of Object.entries(s.drafts)) {
            if (k.startsWith(prefix)) {
              removed += 1
              continue
            }
            next[k] = v
          }
          if (removed === 0) return s
          return { drafts: next }
        }),
    }),
    {
      name: 'ai_alm_gate_drafts_v1',
      storage: {
        getItem: (name) => {
          const raw = quotaSafeStorage.getItem(name) as string | null
          return raw ? JSON.parse(raw) : null
        },
        setItem: (name, value) => {
          quotaSafeStorage.setItem(name, JSON.stringify(value))
        },
        removeItem: (name) => quotaSafeStorage.removeItem(name),
      },
      // Slim down the persisted slice: keep only the architect's
      // mutated fields per decision, drop server-derived ``payload``
      // and ``question``. Cuts per-project G4 storage from ~500 KB
      // to ~5 KB.
      //
      // The cast here is intentional — ``partialize``'s return type
      // is declared as the full state shape, but Zustand only
      // round-trips what ``partialize`` returns through storage.
      // The in-memory state retains the full ``GateDecisionRecord``
      // shape via ``reconcileDraftWithGate``'s merge below.
      partialize: ((state: GateDraftStore) => ({
        drafts: Object.fromEntries(
          Object.entries(state.drafts).map(([k, v]) => [
            k,
            v.map(slimDecision),
          ]),
        ),
      })) as unknown as (state: GateDraftStore) => GateDraftStore,
    },
  ),
)

// Stable empty-array sentinel. Returning `s.drafts[key] ?? []` directly
// from a Zustand selector creates a new `[]` reference on every render
// when the draft is absent, which trips useSyncExternalStore's snapshot
// equality check and produces "getSnapshot should be cached" + a
// "Maximum update depth exceeded" infinite render loop.
const EMPTY_DRAFT: GateDecisionRecord[] = []

export function useGateDraft(key: GateDraftKey): GateDecisionRecord[] {
  return useGateDraftStore((s) => s.drafts[key] ?? EMPTY_DRAFT)
}

// ---------------------------------------------------------------------- //
// reconcileDraftWithGate — single-doorway helper used by every gate
// component to seed its draft from the server payload.
//
// Behaviour:
//   * No persisted draft for this key → seed from ``gate.decisions``.
//   * Persisted draft exists AND its decision-id set is a subset of the
//     server's id set → keep the draft (the user has unsaved edits we
//     must preserve across remounts/reboots).
//   * Persisted draft exists but contains decision ids the server does
//     NOT emit (stale from a deleted/recreated project, or a re-emit
//     that changed the gate shape) → discard the draft and reseed
//     from the server. We do this even when the server's decisions
//     all share a status of ``pending`` so a brand-new project never
//     inherits a recreated id's prior answers.
//
// Returns ``true`` when the draft was reseeded (caller can use this
// for telemetry / toasts), ``false`` when the existing draft was kept
// or the seed was a no-op.
// ---------------------------------------------------------------------- //
export function reconcileDraftWithGate(
  key: GateDraftKey,
  gate: Pick<Gate, 'decisions'> | null | undefined,
): boolean {
  if (!gate) return false
  const store = useGateDraftStore.getState()
  const existing = store.drafts[key]
  const fresh: GateDecisionRecord[] = gate.decisions.map((d) => ({ ...d }))

  if (!existing || existing.length === 0) {
    store.setDraft(key, fresh)
    return true
  }

  // The draft is "compatible" iff every id it carries is also in the
  // server's payload. If the server dropped or renamed a decision, the
  // draft is stale.
  const serverIds = new Set(gate.decisions.map((d) => d.id))
  const compatible = existing.every((d) => serverIds.has(d.id))
  if (!compatible) {
    store.setDraft(key, fresh)
    return true
  }

  // Compatible path. Build the merged list from the SERVER's decisions
  // — NOT by mapping over ``existing`` — for two reasons:
  //
  //   1. The persisted slice strips ``payload`` and ``question`` to stay
  //      under the localStorage quota (see ``partialize`` above), so on
  //      a fresh page load the rehydrated ``existing`` is a slim shape
  //      with those fields missing. We re-merge the server's
  //      ``payload``/``question`` so every ``useGateDraft`` consumer
  //      sees the full ``GateDecisionRecord`` shape in memory.
  //
  //   2. The server may have GAINED decisions since the draft was last
  //      written — a re-emit / "Submit & Reevaluate" adds fresh Critic
  //      findings. The draft's id set is still a SUBSET of the server's,
  //      so the compatibility check above passes, but mapping over
  //      ``existing`` would SILENTLY DROP the new server rows. The
  //      submission then omits them and the backend 422s with
  //      "no choice submitted" — a dead end the architect can never
  //      clear (and which compounds every reevaluate). Iterating the
  //      server set guarantees newly emitted findings are always present
  //      (seeded pending) so they remain decidable + submittable.
  //
  // The architect's mutations (``answer`` / ``status`` / ``skip_reason``
  // / ``rejection_reason`` / edited payload) are preserved verbatim for
  // rows they already touched; only server-derived heavy fields are
  // refreshed, and brand-new server rows are adopted as-is.
  const existingById = new Map(existing.map((d) => [d.id, d]))
  let needsMerge = gate.decisions.length !== existing.length
  const merged = gate.decisions.map((server) => {
    const draft = existingById.get(server.id)
    if (!draft) {
      // New server decision the draft never carried → adopt verbatim so
      // the submission can't omit it.
      return { ...server }
    }
    // Detect whether the slim-hydration ate any server-derived fields.
    if (draft.payload == null && server.payload != null) needsMerge = true
    if (!draft.question && server.question) needsMerge = true
    return {
      ...draft,
      payload: draft.payload ?? server.payload ?? null,
      question: draft.question || server.question || '',
    }
  })
  if (needsMerge) {
    store.setDraft(key, merged)
  }
  return false
}
